import * as React from 'react';
import { cn } from '../utils/cn';

/*
 * Row/Col grid system — Tailwind replacement for antd's 24-column grid.
 * Uses flexbox with 24 columns, matching antd's column system.
 *
 * Row props compat: type="flex", gutter, align, justify
 * Col props compat: span, xs, sm, md, lg, xl, offset
 */

// ─── Shared breakpoint tracking ─────────────────────────────────────────

const BP = { xs: 0, sm: 576, md: 768, lg: 992, xl: 1200 } as const;
type BPKey = keyof typeof BP;
const BP_ORDER: BPKey[] = ['xs', 'sm', 'md', 'lg', 'xl'];

let _currentBP: BPKey = 'md';
const _listeners = new Set<() => void>();

function _calcBP(): BPKey {
  const w = window.innerWidth;
  if (w >= 1200) return 'xl';
  if (w >= 992) return 'lg';
  if (w >= 768) return 'md';
  if (w >= 576) return 'sm';
  return 'xs';
}

if (typeof window !== 'undefined') {
  _currentBP = _calcBP();
  window.addEventListener('resize', () => {
    const next = _calcBP();
    if (next !== _currentBP) {
      _currentBP = next;
      _listeners.forEach(fn => fn());
    }
  });
}

function useBreakpoint(): BPKey {
  const [bp, setBp] = React.useState(_currentBP);
  React.useEffect(() => {
    const update = () => setBp(_currentBP);
    _listeners.add(update);
    update();
    return () => { _listeners.delete(update); };
  }, []);
  return bp;
}

// ─── Row ────────────────────────────────────────────────────────────────

export interface RowProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Layout type — "flex" is default (antd compat) */
  type?: 'flex';
  /** Gap between columns in pixels, or [horizontal, vertical] */
  gutter?: number | [number, number];
  /** Vertical alignment */
  align?: 'top' | 'middle' | 'bottom' | 'stretch';
  /** Horizontal distribution */
  justify?: 'start' | 'end' | 'center' | 'space-around' | 'space-between' | 'space-evenly';
}

const alignMap: Record<string, string> = {
  top: 'items-start',
  middle: 'items-center',
  bottom: 'items-end',
  stretch: 'items-stretch',
};

const justifyMap: Record<string, string> = {
  start: 'justify-start',
  end: 'justify-end',
  center: 'justify-center',
  'space-around': 'justify-around',
  'space-between': 'justify-between',
  'space-evenly': 'justify-evenly',
};

export const Row = React.forwardRef<HTMLDivElement, RowProps>(
  ({ className, gutter, align, justify, style, children, type: _type, ...props }, ref) => {
    const gutterStyle: React.CSSProperties = {};

    if (gutter) {
      const [gx, gy] = Array.isArray(gutter) ? gutter : [gutter, 0];
      if (gx) {
        gutterStyle.marginLeft = -(gx / 2);
        gutterStyle.marginRight = -(gx / 2);
      }
      if (gy) {
        gutterStyle.rowGap = gy;
      }
    }

    return (
      <div
        ref={ref}
        className={cn(
          'flex flex-wrap',
          align && alignMap[align],
          justify && justifyMap[justify],
          className
        )}
        style={{ ...gutterStyle, ...style }}
        {...props}
      >
        {gutter
          ? React.Children.map(children, (child) => {
              if (React.isValidElement<ColProps>(child)) {
                const [gx] = Array.isArray(gutter) ? gutter : [gutter, 0];
                return React.cloneElement(child, {
                  style: {
                    paddingLeft: gx / 2,
                    paddingRight: gx / 2,
                    ...child.props.style,
                  },
                });
              }
              return child;
            })
          : children}
      </div>
    );
  }
);
Row.displayName = 'Row';

// ─── Col ────────────────────────────────────────────────────────────────

export interface ColProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Column span (1-24), 24 = full width */
  span?: number;
  /** Offset in columns */
  offset?: number;
  /** Responsive: < 576px */
  xs?: number | { span: number; offset?: number };
  /** Responsive: >= 576px */
  sm?: number | { span: number; offset?: number };
  /** Responsive: >= 768px */
  md?: number | { span: number; offset?: number };
  /** Responsive: >= 992px */
  lg?: number | { span: number; offset?: number };
  /** Responsive: >= 1200px */
  xl?: number | { span: number; offset?: number };
}

function spanToPercent(span: number): string {
  return `${(span / 24) * 100}%`;
}

function getSpan(val: number | { span: number; offset?: number }): number {
  return typeof val === 'object' ? val.span : val;
}

function getOffset(val: number | { span: number; offset?: number }): number | undefined {
  return typeof val === 'object' ? val.offset : undefined;
}

export const Col = React.forwardRef<HTMLDivElement, ColProps>(
  ({ className, span, offset, xs, sm, md, lg, xl, style, children, ...props }, ref) => {
    const bp = useBreakpoint();
    const colStyle: React.CSSProperties = { ...style };

    // Cascade responsive breakpoints (mobile-first, like antd)
    const bpMap: Record<BPKey, number | { span: number; offset?: number } | undefined> = { xs, sm, md, lg, xl };
    let effectiveSpan = span;
    let effectiveOffset = offset;

    for (const key of BP_ORDER) {
      const val = bpMap[key];
      if (val !== undefined) {
        effectiveSpan = getSpan(val);
        const off = getOffset(val);
        if (off !== undefined) effectiveOffset = off;
      }
      if (key === bp) break;
    }

    if (effectiveSpan !== undefined) {
      colStyle.width = spanToPercent(effectiveSpan);
      colStyle.maxWidth = spanToPercent(effectiveSpan);
      colStyle.flex = `0 0 ${spanToPercent(effectiveSpan)}`;
    }

    if (effectiveOffset) {
      colStyle.marginLeft = spanToPercent(effectiveOffset);
    }

    return (
      <div
        ref={ref}
        className={cn(className)}
        style={colStyle}
        {...props}
      >
        {children}
      </div>
    );
  }
);
Col.displayName = 'Col';
